import { defineAsyncComponent } from "vue"

import ManagerLayout from "~/components/layout/ManagerLayout.vue"
import ViewAbstract from "~/components/abstracts/ViewAbstract.vue"
import FormAbstract from "~/components/abstracts/FormAbstract.vue"

export const createRouteItem = (prefix, routes, layout) => {
    return {
        path: `/${prefix}`,
        name: prefix,
        component: layout,
        children: routes.flatMap((route) => {
            return {
                path: route.path || "",
                name: `${prefix}.${route.name || route.component}`,
                component: () =>
                    import(`~/pages/${prefix}/${route.component}.vue`),
            }
        }),
    }
}

export const generateResourceRoutes = (resource) => {
    const { endpoint, title, store } = resource

    return [
        {
            path: `/manager/${endpoint}`,
            component: ManagerLayout,
            children: [
                {
                    path: ":id",
                    name: `manager.${endpoint}.view`,
                    component: ViewAbstract,
                    props: (route) => ({
                        store,
                        route,
                        type: "view",
                        showDelete: store().config.showDelete ?? true,
                        resourceComponent: defineAsyncComponent(
                            () =>
                                import(
                                    `../components/resources/${title}/${title}View.vue`
                                ),
                        ),
                    }),
                },
                {
                    path: "create",
                    name: `manager.${endpoint}.create`,
                    component: FormAbstract,
                    props: (route) => ({
                        store,
                        route,
                        type: "create",
                        resourceComponent: defineAsyncComponent(
                            () =>
                                import(
                                    `../components/resources/${title}/${title}Form.vue`
                                ),
                        ),
                    }),
                },
                {
                    path: ":id/edit",
                    name: `manager.${endpoint}.edit`,
                    component: FormAbstract,
                    props: (route) => ({
                        store,
                        route,
                        type: "edit",
                        resourceComponent: defineAsyncComponent(
                            () =>
                                import(
                                    `../components/resources/${title}/${title}Form.vue`
                                ),
                        ),
                    }),
                },
            ],
        },
    ]
}

/**
 * Vérifie si l'utilisateur possède les rôles requis
 * Supporte les conditions imbriquées pour combiner AND et OR
 * @param routeRoles - Configuration des rôles (array, object ou undefined)
 * @param userRoles - Rôles de l'utilisateur
 * @returns true si l'utilisateur a les permissions requises
 */
const hasRequiredRoles = (routeRoles, userRoles) => {
    // Pas de rôles requis ou rôles "*" -> accessible à tous
    if (!routeRoles || routeRoles?.includes?.("*")) return true

    // Syntaxe objet : { all: [...], any: [...] }
    if (typeof routeRoles === "object" && !Array.isArray(routeRoles)) {
        // ET logique : l'utilisateur doit avoir TOUS les rôles/conditions
        if (routeRoles.all) {
            return routeRoles.all.every((role) => {
                // Si c'est un objet imbriqué, appel récursif
                if (typeof role === "object") {
                    return hasRequiredRoles(role, userRoles)
                }
                // Sinon vérification simple du rôle
                return userRoles?.includes(role)
            })
        }
        // OU logique : l'utilisateur doit avoir AU MOINS UN des rôles/conditions
        if (routeRoles.any) {
            return routeRoles.any.some((role) => {
                // Si c'est un objet imbriqué, appel récursif
                if (typeof role === "object") {
                    return hasRequiredRoles(role, userRoles)
                }
                // Sinon vérification simple du rôle
                return userRoles?.includes(role)
            })
        }
        return false
    }

    // Syntaxe array (rétrocompatibilité) : OU logique par défaut
    if (Array.isArray(routeRoles)) {
        return routeRoles.some((role) => userRoles?.includes(role))
    }

    return false
}

export const drawerMenu = (routes, prefix, userRoles) => {
    return routes
        .filter((o) => {
            const isDrawer = o?.drawer ?? true
            if (!isDrawer) return false

            return hasRequiredRoles(o.roles, userRoles)
        })
        .map((route) => {
            return {
                text:
                    route?.text ||
                    route.path.charAt(0).toUpperCase() + route.path.slice(1),
                to: prefix + "." + route.component,
                icon: route?.icon || "mdi-vector-point",
            }
        })
}
